import React, { useState, useEffect, useRef } from 'react';

// Use environment variable for API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function Header({ onSearchResults, onError, onLoading }) {
    const [startStation, setStartStation] = useState('');
    const [endStation, setEndStation] = useState('');
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(() => {
        const now = new Date();
        // Get Warsaw time in YYYY-MM-DDTHH:MM:SS format
        return new Intl.DateTimeFormat('sv-SE', {
            timeZone: 'Europe/Warsaw',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(now).replace(' ', 'T');
    });

    // Autocomplete states
    const [startStationSuggestions, setStartStationSuggestions] = useState([]);
    const [endStationSuggestions, setEndStationSuggestions] = useState([]);
    const [showStartSuggestions, setShowStartSuggestions] = useState(false);
    const [showEndSuggestions, setShowEndSuggestions] = useState(false);
    const [validStartStations, setValidStartStations] = useState([]);
    const [validEndStations, setValidEndStations] = useState([]);
    const [manualSelection, setManualSelection] = useState({ start: false, end: false });
    
    // Refs for input elements
    const startInputRef = useRef(null);
    const endInputRef = useRef(null);

    // Fetch station suggestions from API
    const fetchStationSuggestions = async (query) => {
        if (query.length < 2) return [];
        
        try {
            const response = await fetch(
                `${API_BASE_URL}/stations-hints?partial_name=${encodeURIComponent(query)}`
            );
            
            if (!response.ok) {
                return [];
            }
            
            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('Error fetching station suggestions:', err);
            return [];
        }
    };

    // Handle start station input change
    const handleStartStationChange = async (e) => {
        const value = e.target.value;
        setStartStation(value);
        setManualSelection(prev => ({ ...prev, start: false })); // Reset manual selection flag

        if (value.length >= 2) {
            const suggestions = await fetchStationSuggestions(value);
            setStartStationSuggestions(suggestions);
            setValidStartStations(suggestions);
            setShowStartSuggestions(true);
        } else {
            setStartStationSuggestions([]);
            setShowStartSuggestions(false);
            setValidStartStations([]);
        }
    };

    // Handle end station input change
    const handleEndStationChange = async (e) => {
        const value = e.target.value;
        setEndStation(value);
        setManualSelection(prev => ({ ...prev, end: false })); // Reset manual selection flag

        if (value.length >= 2) {
            const suggestions = await fetchStationSuggestions(value);
            setEndStationSuggestions(suggestions);
            setValidEndStations(suggestions);
            setShowEndSuggestions(true);
        } else {
            setEndStationSuggestions([]);
            setShowEndSuggestions(false);
            setValidEndStations([]);
        }
    };

    // Handle input blur (when user clicks away)
    const handleStartStationBlur = (e) => {
        // Don't auto-complete if clicking on a suggestion (they have onMouseDown which prevents this)
        setTimeout(() => {
            if (!manualSelection.start && startStation && startStationSuggestions.length > 0) {
                const bestMatch = startStationSuggestions.find(suggestion => 
                    suggestion.toLowerCase().startsWith(startStation.toLowerCase())
                ) || startStationSuggestions[0];
                setStartStation(bestMatch);
            }
            setShowStartSuggestions(false);
        }, 100); // Small delay to allow onClick to process first
    };

    const handleEndStationBlur = (e) => {
        // Don't auto-complete if clicking on a suggestion (they have onMouseDown which prevents this)
        setTimeout(() => {
            if (!manualSelection.end && endStation && endStationSuggestions.length > 0) {
                const bestMatch = endStationSuggestions.find(suggestion => 
                    suggestion.toLowerCase().startsWith(endStation.toLowerCase())
                ) || endStationSuggestions[0];
                setEndStation(bestMatch);
            }
            setShowEndSuggestions(false);
        }, 100); // Small delay to allow onClick to process first
    };

    // Handle suggestion selection
    const selectStartStation = (station) => {
        setStartStation(station);
        setManualSelection(prev => ({ ...prev, start: true })); // Mark as manually selected
        setShowStartSuggestions(false);
    };

    const selectEndStation = (station) => {
        setEndStation(station);
        setManualSelection(prev => ({ ...prev, end: true })); // Mark as manually selected
        setShowEndSuggestions(false);
    };

    // Step 1: Search for trains
    const handleSearch = async (e) => {
        e.preventDefault();
        
        if (!startStation || !endStation) {
            onError('Please enter both start and end stations');
            return;
        }

        setLoading(true);
        onLoading(true);
        onError('');

        try {
            const response = await fetch(
                `${API_BASE_URL}/search-train?start_station=${encodeURIComponent(startStation)}&end_station=${encodeURIComponent(endStation)}&date=${encodeURIComponent(date)}`
            );
            
            if (!response.ok) {
                throw new Error('Failed to search trains');
            }
            
            const data = await response.json();
            onSearchResults(data, startStation, endStation);
        } catch (err) {
            onError('Error searching for trains: ' + err.message);
        } finally {
            setLoading(false);
            onLoading(false);
        }
    };

    return (
        <header>
            <div className="header-top-container">
                <h1 onClick={() => window.location.href = '/'}>WhereToSeat</h1>
                <nav>
                    <a href="/">Home</a>
                </nav>
            </div>

            <h2>Where are we going?</h2>

            {/* Search Form */}
            <div className="header-search">
                <form onSubmit={handleSearch}>
                    <div className="input-group">
                        <div className="input-with-suggestions" ref={startInputRef}>
                            <input 
                                type="text" 
                                placeholder="Start Station" 
                                value={startStation}
                                onChange={handleStartStationChange}
                                onBlur={handleStartStationBlur}
                                disabled={loading}
                                autoComplete="off"
                            />
                            {showStartSuggestions && startStationSuggestions.length > 0 && (
                                <div className="suggestions-dropdown">
                                    {startStationSuggestions.map((station, index) => (
                                        <div 
                                            key={index}
                                            className="suggestion-item"
                                            onMouseDown={(e) => {
                                                e.preventDefault(); // Prevent blur event
                                                selectStartStation(station);
                                            }}
                                        >
                                            {station}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="input-with-suggestions" ref={endInputRef}>
                            <input 
                                type="text" 
                                placeholder="End Station" 
                                value={endStation}
                                onChange={handleEndStationChange}
                                onBlur={handleEndStationBlur}
                                disabled={loading}
                                autoComplete="off"
                            />
                            {showEndSuggestions && endStationSuggestions.length > 0 && (
                                <div className="suggestions-dropdown">
                                    {endStationSuggestions.map((station, index) => (
                                        <div 
                                            key={index}
                                            className="suggestion-item"
                                            onMouseDown={(e) => {
                                                e.preventDefault(); // Prevent blur event
                                                selectEndStation(station);
                                            }}
                                        >
                                            {station}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <input 
                            type="datetime-local" 
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            disabled={loading}
                            title="Select departure date and time (optional - defaults to current time)"
                            placeholder="Optional: Select date and time"
                        />
                        <button 
                            type="submit" 
                            className="search-button"
                            disabled={loading}
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </form>
            </div>
        </header>
    )
}

export default Header;